import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Snackbar } from 'src/components/snackbar';
import { IGcfComponentConfig } from 'src/interfaces/Component';
import { lang } from 'src/utils';

import {
  buildScheduleItemOverrideChangeItem,
  buildScheduleItemOverrideChangeSetPayload,
  findProductByUid,
  getProductImageStyleAndOutageStamp,
  hasOverrideDateValidationError,
} from '../../MenuBoardModificationPage.helper';
import {
  IOverrideChangeSet,
  ISelectedSectionDetailInfo,
} from '../../MenuBoardModificationPage.interface';
import {
  computeUpdatedComponentConfig,
  IComputeUpdatedConfigResult,
} from '../productOverrideRightSideBar/ProductOverrideRightSideBar.config';
import { IAttributeChanges } from '../productOverrideRightSideBar/ProductOverrideRightSideBar.interface';

// ── Private helper (moved from MenuBoardModificationPage.helper.ts) ───────
// Only updateItemById moves here — it's exclusively used inside the extracted
// block. The other shared helpers (buildScheduleItemOverrideChangeItem,
// buildScheduleItemOverrideChangeSetPayload, ScheduleItemOverrideInput) stay
// exported in the main helper because createValidatedOverrideChangeSet also
// uses them.

const updateItemById = <T>(
  items: T[],
  targetId: string | number,
  getId: (item: T) => string | number,
  updater: (item: T) => T,
): T[] => {
  let changed = false;
  const next = items.map((item) => {
    if (String(getId(item)) === String(targetId)) {
      changed = true;

      return updater(item);
    }

    return item;
  });

  return changed ? next : items;
};

// ── Public hook ───────────────────────────────────────────────────────────

const CHANGESET_DEBOUNCE_MS = 1000;

interface UseProductAttributeChangesetParams {
  selectedScheduleItem: ISelectedSectionDetailInfo | undefined;
  overrideChangeSets: IOverrideChangeSet[];
  setOverrideChangeSets: React.Dispatch<
    React.SetStateAction<IOverrideChangeSet[]>
  >;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateChangeSet: any; // UseMutationResult from useUpdateChangeSet
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createChangeSet: any; // UseMutationResult from useCreateChangeSet
  locationGroupId: string | undefined;
  locationCode: string;
  changeSetDaypartKey: string;
}

export const useProductAttributeChangeset = ({
  selectedScheduleItem,
  overrideChangeSets,
  setOverrideChangeSets,
  updateChangeSet,
  createChangeSet,
  locationGroupId,
  locationCode,
  changeSetDaypartKey,
}: UseProductAttributeChangesetParams) => {
  const productImageStyleAndOutageStamp = useMemo(
    () => getProductImageStyleAndOutageStamp(selectedScheduleItem),
    [selectedScheduleItem],
  );

  // Ref for stable access inside debounced function
  const overrideChangeSetsRef = useRef(overrideChangeSets);
  useEffect(() => {
    overrideChangeSetsRef.current = overrideChangeSets;
  }, [overrideChangeSets]);

  // Ref for stable access to selectedScheduleItem inside debounced callback.
  const selectedScheduleItemRef = useRef(selectedScheduleItem);
  useEffect(() => {
    selectedScheduleItemRef.current = selectedScheduleItem;
  }, [selectedScheduleItem]);

  // Refs for mutation hooks and route params consumed inside the debounced
  // callback. The debounced fn is captured once via `useRef(debounce(...))` so
  // it would otherwise close over the values from the first render.
  const submitContextRef = useRef({
    updateChangeSet,
    createChangeSet,
    locationGroupId,
    locationCode,
    changeSetDaypartKey,
  });
  useEffect(() => {
    submitContextRef.current = {
      updateChangeSet,
      createChangeSet,
      locationGroupId,
      locationCode,
      changeSetDaypartKey,
    };
  }, [
    updateChangeSet,
    createChangeSet,
    locationGroupId,
    locationCode,
    changeSetDaypartKey,
  ]);

  const updateOptimisticConfig = useCallback(
    (
      changes: IAttributeChanges,
      selectProductKey: string,
    ):
      | (IComputeUpdatedConfigResult & {
          overrideItem: IOverrideChangeSet['changeSetItem'];
          matchedChangeSetId: string | number;
        })
      | undefined => {
      if (
        !selectedScheduleItem?.component?.config ||
        !selectedScheduleItem.componentId
      ) {
        return undefined;
      }

      // Bind the edit to the card that owns the edited product's uid. Cards on
      // the same schedule item + component share scheduleItemId/componentId, so
      // the product uid — unique per card — is the only reliable discriminator.
      const matchedChangeSet = overrideChangeSetsRef.current.find((cs) =>
        Boolean(
          findProductByUid(
            cs.changeSetItem.component?.config as
              | IGcfComponentConfig
              | undefined,
            selectProductKey,
          ),
        ),
      );

      if (!matchedChangeSet) {
        return undefined;
      }

      const overrideItem = matchedChangeSet.changeSetItem;
      const baseConfig =
        overrideItem.component?.config ?? selectedScheduleItem.component.config;

      const { componentConfig, scheduleItemOverrideProductVariations } =
        computeUpdatedComponentConfig(baseConfig, selectProductKey, changes);

      setOverrideChangeSets((current) =>
        updateItemById(
          current,
          overrideItem.id,
          (cs) => cs.changeSetItem.id,
          (cs) =>
            cs.changeSetItem.component
              ? {
                  ...cs,
                  changeSetItem: {
                    ...cs.changeSetItem,
                    // A product attribute change (variations / sizes / image) is
                    // an edit, so the override counts as touched for validation.
                    hasProductEdit: true,
                    component: {
                      ...cs.changeSetItem.component,
                      config: componentConfig,
                    },
                  },
                }
              : cs,
        ),
      );

      return {
        overrideItem,
        matchedChangeSetId: matchedChangeSet.changeSetItem.id,
        componentConfig,
        scheduleItemOverrideProductVariations,
      };
    },
    [selectedScheduleItem, setOverrideChangeSets],
  );

  const submitChangeSetToApiRef = useRef(
    debounce(
      async (
        params: IComputeUpdatedConfigResult & {
          overrideItem: IOverrideChangeSet['changeSetItem'];
          matchedChangeSetId: string | number;
        },
      ) => {
        const {
          overrideItem,
          matchedChangeSetId,
          componentConfig,
          scheduleItemOverrideProductVariations,
        } = params;

        // Read from ref to avoid stale closure
        const currentScheduleItem = selectedScheduleItemRef.current;

        if (hasOverrideDateValidationError(currentScheduleItem, overrideItem)) {
          return;
        }

        const freshChangeSet = overrideChangeSetsRef.current.find(
          (cs) => cs.changeSetItem.id === matchedChangeSetId,
        );

        if (!freshChangeSet) {
          return;
        }

        const overrideInput = {
          id: overrideItem.id as string,
          endDate: overrideItem.endDate,
          startDate: overrideItem.startDate,
          componentId: overrideItem.componentId!,
          scheduleItemId: overrideItem.scheduleItemId!,
          componentConfig,
          scheduleItemOverrideProductVariations,
        };

        try {
          const changeItem = buildScheduleItemOverrideChangeItem({
            override: overrideInput,
          });

          const submitContext = submitContextRef.current;

          if (freshChangeSet.previewChangeSetId) {
            await submitContext.updateChangeSet.mutateAsync({
              changeSetId: Number(freshChangeSet.previewChangeSetId),
              payload: { changes: [changeItem] },
            });
          } else {
            const payload = buildScheduleItemOverrideChangeSetPayload({
              locationGroupId: Number(submitContext.locationGroupId),
              locationCode: submitContext.locationCode,
              daypartId: Number(submitContext.changeSetDaypartKey),
              override: overrideInput,
            });

            const response = await submitContext.createChangeSet.mutateAsync(
              payload,
            );

            if (!response?.id) {
              return;
            }

            setOverrideChangeSets((current) =>
              updateItemById(
                current,
                overrideItem.id,
                (cs) => cs.changeSetItem.id,
                (cs) =>
                  !cs.previewChangeSetId
                    ? { ...cs, previewChangeSetId: String(response.id) }
                    : cs,
              ),
            );
          }
        } catch {
          Snackbar.error(lang.failedSaveText);
        }
      },
      CHANGESET_DEBOUNCE_MS,
    ),
  );

  // Cancel pending debounced API call when the selected schedule item changes,
  // preventing a stale call from applying changes to the wrong product.
  // Depend on the stable id — selectedScheduleItem itself is a new object
  // reference after every optimistic config update, which would otherwise
  // cancel the call we just scheduled.
  useEffect(() => {
    submitChangeSetToApiRef.current.cancel();
  }, [selectedScheduleItem?.id]);

  // Cancel on unmount
  useEffect(() => {
    const debounced = submitChangeSetToApiRef.current;

    return () => {
      debounced.cancel();
    };
  }, []);

  const handleProductAttributeChange = useCallback(
    async (changes: IAttributeChanges, selectProductKey: string) => {
      const result = updateOptimisticConfig(changes, selectProductKey);
      if (result) {
        submitChangeSetToApiRef.current(result);
      }
    },
    [updateOptimisticConfig],
  );

  return {
    productImageStyleAndOutageStamp,
    handleProductAttributeChange,
  };
};
